import React from 'react';
import {
  Box, Table, TableHead, TableRow, TableCell, TableBody, Typography, Alert,
} from '@mui/material';
import StatusChip from '../StatusChip';
import InvButton from '../InvButton';
import WoBreakupList from './WoBreakupList';
import { formatCurrency } from '../../utils';
import { rankWoQuotes, woQuoteRateForItem, asWholeNumber } from '../../utils/woEntity';
import { IPD_COLORS } from '../../theme';

export default function WoQuoteComparison({
  items = [], quotes = [], canAward, onAward,
}) {
  const ranked = rankWoQuotes(quotes);

  if (!quotes.length) {
    return (
      <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
        No quotations yet. Vendors sign in on the vendor link with OTP and submit rates here.
      </Typography>
    );
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Lowest total is L1. Allocate one vendor to issue this work order with their rates.
      </Alert>
      <Box sx={{ overflowX: 'auto', mb: 2.5 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Rank</TableCell>
              <TableCell>Vendor</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Breakup</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {ranked.map((q) => (
              <TableRow key={q.id} sx={q.rank === 1 ? { backgroundColor: IPD_COLORS.success?.bg || '#E6F4EC' } : undefined}>
                <TableCell>
                  <StatusChip status={q.rank === 1 ? 'AWARDED' : 'PUBLISHED'} label={q.rank_label} />
                </TableCell>
                <TableCell>{q.vendor_name || q.vendor_id || '—'}</TableCell>
                <TableCell align="right">{formatCurrency(q.grand_total)}</TableCell>
                <TableCell sx={{ minWidth: 200, verticalAlign: 'top' }}>
                  <WoBreakupList text={q.breakup} />
                </TableCell>
                <TableCell>
                  <StatusChip status={q.status || 'SUBMITTED'} />
                </TableCell>
                <TableCell>
                  {canAward && q.status !== 'ACCEPTED' && (
                    <InvButton
                      size="small"
                      tone="mint"
                      onClick={() => onAward?.(q)}
                    >
                      Allocate
                    </InvButton>
                  )}
                  {q.status === 'ACCEPTED' && <StatusChip status="AWARDED" label="Awarded" />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      {items.length > 0 && ranked.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1, color: IPD_COLORS.textPrimary }}>
            Line rates
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item / service</TableCell>
                {ranked.map((q) => (
                  <TableCell key={q.id} align="right">{q.vendor_name || q.rank_label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, i) => (
                <TableRow key={item.id || i}>
                  <TableCell>{item.item_name || '—'}</TableCell>
                  {ranked.map((q) => {
                    const rate = woQuoteRateForItem(q, item);
                    return (
                      <TableCell key={q.id} align="right">
                        {rate == null ? '—' : formatCurrency(asWholeNumber(rate) || rate)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
